import { Illustration, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { share } from "@src/appV2/NativeAdapters/share.adapter";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useCreateShareLink } from "@src/appV2/Referral/api/useCreateShareLink";

import { type FeedbackStep } from "./feedbackStep";
import { useInterviewAttendanceSource } from "./useInterviewAttendanceSource";

interface ReferralPromptContentProps {
  step?: FeedbackStep;
  placementCandidate: PlacementCandidate;
  interview?: InterviewWithDetails;
}

export function ReferralPromptContent(props: ReferralPromptContentProps) {
  const { step, placementCandidate, interview } = props;

  const source = useInterviewAttendanceSource();
  const { mutateAsync: createShareLink, isLoading } = useCreateShareLink();

  const { amount: REFERRAL_BONUS_AMOUNT } = useCbhFlag(
    CbhFeatureFlag.OFFER_OUTCOME_REFERRAL_AMOUNT,
    {
      defaultValue: { enabled: false, amount: 20 },
    }
  );

  const eventContext = {
    step,
    placementCandidate,
    interview,
    source,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_REFERRAL_VIEWED, eventContext);

  async function handleShareClick() {
    try {
      logEvent(
        APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_REFERRAL_SHARE_CLICKED,
        eventContext
      );

      const { data: referralLinkDataArray } = await createShareLink({ isReferredToHiring: true });

      // eslint-disable-next-line prefer-destructuring
      const referralLinkData = referralLinkDataArray[0];

      if (isDefined(referralLinkData)) {
        await share({
          title: "Join Clipboard",
          text: `Hey! I just got a job through Clipboard Health. Use my link to apply and we'll both get $${REFERRAL_BONUS_AMOUNT} once you complete an interview.\n\n${referralLinkData.attributes.referralLink}`,
        });
      }
    } catch {
      // Ignore errors from share dialog dismissal
    }
  }

  return (
    <>
      <Stack
        spacing={3}
        alignItems="center"
        justifyContent="center"
        sx={{ paddingX: 8, paddingY: 4 }}
      >
        <Box sx={{ transform: "scale(1.2)", transformOrigin: "center" }}>
          <Illustration type="referral-bonus" />
        </Box>
        <Stack spacing={2} textAlign="center">
          <Text variant="h3">Congrats on the offer!</Text>
          <Text paddingTop={3}>Know someone looking for a permanent job?</Text>
          <Text>
            You both earn{" "}
            <Box
              component="span"
              sx={(theme) => ({
                backgroundColor: theme.palette.intent?.success.background,
                color: theme.palette.intent?.success.text,
                fontWeight: 500,
                paddingX: 1,
                paddingY: 0.25,
                borderRadius: "100px",
              })}
            >
              ${REFERRAL_BONUS_AMOUNT}
            </Box>{" "}
            after their first interview.
          </Text>
        </Stack>
      </Stack>
      <DialogFooter orientation="horizontal" sx={{ px: 8, pb: 8, pt: 4 }}>
        <Stack spacing={4} width="100%">
          <LoadingButton
            variant="contained"
            size="large"
            isLoading={isLoading}
            onClick={() => {
              handleShareClick();
            }}
          >
            Share referral link
          </LoadingButton>
          <Text
            variant="caption"
            color={(theme) => theme.palette.text.tertiary}
            sx={{ textAlign: "center" }}
          >
            Bonus paid per referral after their first confirmed interview attendance through
            Clipboard Health. T&Cs apply.
          </Text>
        </Stack>
      </DialogFooter>
    </>
  );
}
